// src/pages/admin/Dashboard.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getDashboardStats, getAllOrders } from "../../services/firestoreService";
import Spinner from "../../components/common/Spinner";
import { format } from "date-fns";
import {
  FiShoppingBag, FiPackage, FiUsers, FiMail,
  FiTrendingUp, FiArrowRight, FiClock,
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";

const STATUS_COLORS = {
  Pending:   "bg-yellow-100 text-yellow-700 border border-yellow-200",
  Confirmed: "bg-blue-100 text-blue-700 border border-blue-200",
  Shipped:   "bg-indigo-100 text-indigo-700 border border-indigo-200",
  Delivered: "bg-green-100 text-green-700 border border-green-200",
  Cancelled: "bg-red-100 text-red-700 border border-red-200",
};

const STATUS_BAR_COLORS = {
  Delivered: "bg-gradient-to-r from-green-400 to-green-600",
  Pending:   "bg-gradient-to-r from-yellow-300 to-yellow-500",
  Cancelled: "bg-gradient-to-r from-red-400 to-red-500",
  Shipped:   "bg-gradient-to-r from-indigo-400 to-indigo-600",
  Confirmed: "bg-gradient-to-r from-blue-400 to-blue-600",
};

const initials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((n) => n[0]).join("").toUpperCase() || "?";

export default function Dashboard() {
  const [stats,        setStats]       = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading,      setLoading]     = useState(true);

  useEffect(() => {
    Promise.all([getDashboardStats(), getAllOrders()]).then(([s, orders]) => {
      setStats(s);
      setRecentOrders(orders.slice(0, 6));
      setLoading(false);
    });
  }, []);

  if (loading) return <Spinner center />;

  const STAT_CARDS = [
    {
      label: "Total Orders", value: stats.totalOrders, icon: FiShoppingBag, to: "/admin/orders",
      gradient: "from-blue-500 via-blue-600 to-indigo-600",
      glow: "shadow-blue-500/30",
    },
    {
      label: "Total Revenue", value: `₹${stats.revenue.toLocaleString("en-IN")}`, icon: FaRupeeSign, to: "/admin/orders",
      gradient: "from-emerald-500 via-green-600 to-teal-600",
      glow: "shadow-emerald-500/30",
    },
    {
      label: "Products", value: stats.totalProducts, icon: FiPackage, to: "/admin/products",
      gradient: "from-orange-400 via-orange-500 to-amber-600",
      glow: "shadow-orange-500/30",
    },
    {
      label: "Registered Users", value: stats.totalUsers, icon: FiUsers, to: "/admin/users",
      gradient: "from-fuchsia-500 via-purple-600 to-violet-600",
      glow: "shadow-purple-500/30",
    },
    {
      label: "Unread Inquiries", value: stats.unreadContacts, icon: FiMail, to: "/admin/contacts",
      gradient: "from-rose-500 via-pink-600 to-red-600",
      glow: "shadow-rose-500/30",
    },
    {
      label: "Pending Orders", value: stats.statusCounts?.Pending || 0, icon: FiTrendingUp, to: "/admin/orders",
      gradient: "from-amber-400 via-yellow-500 to-orange-500",
      glow: "shadow-amber-500/30",
    },
  ];

  return (
    <div>
      {/* ── Header ── */}
      <div className="mb-7 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">Welcome back! Here's what's happening today.</p>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-xs font-medium text-gray-400 bg-white border border-gray-100 rounded-full px-4 py-2 shadow-sm">
          <FiClock className="w-3.5 h-3.5" />
          {format(new Date(), "EEEE, dd MMM yyyy")}
        </div>
      </div>

      {/* ── Stat Cards — bold gradient design, unique color per card ── */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {STAT_CARDS.map(({ label, value, icon: Icon, gradient, glow, to }) => (
          <Link
            key={label}
            to={to}
            className={`group relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br ${gradient}
                        shadow-lg ${glow} hover:shadow-xl hover:-translate-y-1
                        transition-all duration-300`}
          >
            {/* Decorative translucent circles */}
            <div className="pointer-events-none absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10 group-hover:scale-125 transition-transform duration-500" />
            <div className="pointer-events-none absolute -bottom-8 -left-4 w-20 h-20 rounded-full bg-white/10" />
            {/* Diagonal shine on hover */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-white/0 via-white/0 to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

            <div className="relative flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-white/80 font-semibold truncate">{label}</p>
                <p className="text-xl sm:text-2xl font-extrabold text-white mt-1.5 truncate drop-shadow-sm">{value}</p>
              </div>
              <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0 text-white group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300">
                <Icon className="w-5 h-5" />
              </div>
            </div>
            <div className="relative mt-3 flex items-center gap-1 text-[11px] font-bold text-white/70 group-hover:text-white transition-colors">
              View details <FiArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </Link>
        ))}
      </div>

      {/* ── Order Status Breakdown + Recent Orders ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Order Status Breakdown */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-bold text-gray-900 text-base">Order Status Breakdown</h2>
            <span className="text-xs font-medium text-gray-400 bg-gray-50 px-2.5 py-1 rounded-full">
              {stats.totalOrders} total
            </span>
          </div>
          <div className="space-y-4">
            {Object.entries(stats.statusCounts || {}).map(([status, count]) => {
              const pct = stats.totalOrders > 0 ? Math.round((count / stats.totalOrders) * 100) : 0;
              return (
                <div key={status}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="font-semibold text-gray-700">{status}</span>
                    <span className="text-gray-400 text-xs font-medium">{count} orders · {pct}%</span>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${STATUS_BAR_COLORS[status] || "bg-gradient-to-r from-gray-300 to-gray-400"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-bold text-gray-900 text-base">Recent Orders</h2>
            <Link
              to="/admin/orders"
              className="text-xs font-semibold text-primary-600 hover:text-primary-700 flex items-center gap-1 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-full transition-colors"
            >
              View all <FiArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-1">
            {recentOrders.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No orders yet</p>
            ) : (
              recentOrders.map((order) => (
                <div
                  key={order.id}
                  className="flex items-center justify-between gap-3 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50/60 -mx-2 px-2 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {initials(order.userName)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{order.userName}</p>
                      <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <span className="font-mono">#{order.id.slice(0, 8).toUpperCase()}</span>
                        {order.createdAt?.toDate && (
                          <>
                            <span>·</span>
                            <span>{format(order.createdAt.toDate(), "dd MMM, hh:mm a")}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_COLORS[order.status] || "bg-gray-100 text-gray-600 border border-gray-200"}`}>
                      {order.status}
                    </span>
                    <span className="font-bold text-sm text-gray-900 flex items-center gap-0.5">
                      <FaRupeeSign className="w-3 h-3" />{order.total}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}